import clsx from 'clsx';
import { useFormik } from 'formik';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import ParagraphHeader from 'components/ParagraphHeader';
import Button from 'components/common/Button';
import Image from 'components/common/Image';
import BasicLinkComponent from 'components/common/Link';
import Section from 'components/common/Section';
import { BannerInPageFormProps } from 'interfaces/cms/content';
import GTMHelper from 'lib/GTMHelper';
import qflowService from 'lib/cms/qFlowService';
import flowToConfig from 'lib/qFlow/flowToConfig';
import styles from './BannerInPageForm.module.scss';

function BannerInPageForm({
  header,
  title,
  titleColor,
  background,
  qFlowId,
  alignment,
  desktopImage,
  mobileImage,
  button,
}: BannerInPageFormProps) {
  const { push } = useRouter();
  const { t } = useTranslation('common');
  const [config, setConfig] = useState<any>({
    questions: {},
    questionsVisibility: {},
    questionOrder: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const service = new qflowService();

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const setFieldValue = useCallback((field: string, value: any) => {
    formik.setFieldTouched(field);
    formik.setFieldValue(field, value);
  }, []);

  const trackGTMSubmit = useCallback(() => {
    GTMHelper.customEvent('question/submit', {
      webpage: 'in-page-form',
    });
  }, [GTMHelper]);

  useEffect(() => {
    if (qFlowId) {
      GTMHelper.customEvent('inPageForm/question_start', {
        webpage: 'in-page-form',
      });

      const getInititalFlow = async () => {
        const data = await service.getConfigFlow(qFlowId);
        const initialConfig = flowToConfig(data, t);
        const firstQuestion = initialConfig?.questionOrder[0];
        GTMHelper.customEvent('question/display', {
          webpage: 'in-page-form',
          product: 'motor-insurance',
          question: firstQuestion,
        });
        localStorage.removeItem('qFlowInPageForm');
        setConfig(initialConfig);
        setIsLoading(false);
      };
      getInititalFlow().catch(console.error);
    }
  }, [qFlowId]);

  const handleDynamicQuestionPatch = useCallback(async () => {
    GTMHelper.customEvent('inPageForm/question_complete', {
      webpage: 'in-page-form',
    });

    push(button?.buttonUrl as string);
  }, [service]);

  const getAlignment = () => {
    switch (alignment) {
      case 'left':
        return 'top-[50%] left-0 -translate-y-[50%]';
      case 'right':
        return 'top-[50%] right-0 -translate-y-[50%]';
      default:
        return 'top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]';
    }
  };

  const getChildComponents = (() => (
    <div className="flex flex-col w-full p-4 h-full">
      <h3 className="font-bold" style={{ color: titleColor }}>
        {title}
      </h3>
      <div className="grow">
        {!isLoading && (
          <Section
            questions={config?.questions}
            questionVisibility={config?.questionsVisibility}
            formik={formik}
            handleChange={setFieldValue}
            handleDynamicQuestionPatch={handleDynamicQuestionPatch}
          />
        )}
      </div>
      <BasicLinkComponent
        href={
          !button?.buttonPageSlug
            ? button?.buttonUrl
            : button?.buttonPageSlug.slug
        }
        rel={button?.buttonLinkRel}
        isExternalLink={!button?.buttonPageSlug}
        passHref
        dataTestId="in-page-link"
      >
        <Button
          {...button}
          btnStyle="min-w-full"
          isFullWidth
          onClick={trackGTMSubmit}
        />
      </BasicLinkComponent>
    </div>
  ))();

  return (
    <div
      data-testid="banner-in-page-form"
      style={{ backgroundColor: background ?? 'transparent' }}
      className="max-w-[90rem] mx-auto my-0"
    >
      {header && (
        <div className="md:hidden max-w-[69.375rem] mx-auto my-0 px-[1rem] py-[1.9rem] block">
          <ParagraphHeader {...header} />
        </div>
      )}
      <div className="max-w-[90rem] my-0 mx-auto">
        <div className="md:hidden rounded-2xl bg-white shadow-[0_1.875rem_0.625rem_#1066cb1a] mx-[16px] mb-[16px] block">
          {mobileImage && (
            <Image
              src={mobileImage.url}
              width={mobileImage.width}
              height={mobileImage.height}
              alt={mobileImage.alt}
              sizes="100vw"
              className="rounded-t-2xl"
              data-testid="mobile-banner-in-page-form"
            />
          )}
          <div className="h-[403px]">{getChildComponents}</div>
        </div>

        <div className="relative hidden md:block">
          <div
            data-testid="desktop-banner-in-page-form"
            className={clsx(
              'w-[343px] mx-[1.875rem]',
              'bg-white shadow-[0_1.875rem_0.625rem_#1066cb1a] rounded-2xl',
              'absolute z-10 flex flex-col items-center h-[403px]',
              getAlignment()
            )}
          >
            {getChildComponents}
          </div>
          {desktopImage && (
            <div
              className={clsx(
                'w-full overflow-hidden h-[30rem] z-0 relative',
                styles.banner__desktopImage
              )}
            >
              <Image
                src={desktopImage?.url}
                alt={desktopImage?.alt ?? 'banner in page form desktop image'}
                fill
                style={{
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BannerInPageForm;
